import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchingExternalLibrary} from "actions/media";

import _ from "lodash";
import cx from "classnames";
import { ExternalMedia, PreviewExternalMedia } from "../ExternalMedia";

import { Button, Nav, Tab, InputGroup, Form } from "react-bootstrap";
import InfiniteScroll from "components/InfiniteScroll";

import styles from "./externalMediaPalette.module.scss";
import { ShortLoader } from "components/ApplicationLoader";

export const ExternalMediaPalette = ({active, onSelect, orientation, mediaLibraryType, editorSession, target, onClose}) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    active: "image",
    selected: [],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [preview, setPreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const query = encodeURIComponent(searchQuery);
  const uploads = useSelector((state) => _.get(state, "media.uploads")) || [];
  const mediaTarget = useSelector(state => _.get(state, 'media.mediaTarget')) || null;
  const uploadError = useSelector((state) => _.get(state, "media.externalMedia.uploadError"));
  const mediaList = useSelector((state) => _.get(state, "media.externalMedia.items")) || [];
  const loading = useSelector((state) => _.get(state, "media.externalMedia.loading"));
  const loaded = useSelector((state) => _.get(state, "media.externalMedia.loaded", false));
  const loadMore = useSelector((state) => _.get(state, "media.externalMedia.loadMore"));
  const pagination = useSelector((state) => _.get(state, "media.externalMedia.pagination"));
  const hasMoreItems = loadMore && _.get(pagination, "currentPage") < 30;

  const mediaListMemo = useMemo(() => {
    if (state.active === "image") {
      return mediaList.filter(media => media.mediaType === "image");
    } else if (state.active === "video") {
      return mediaList.filter(media => media.mediaType === "video");
    }
    return mediaList;
  }, [mediaList, state.active]);

  useEffect(() => {
    if (active === 'pexels' && (!loaded || state.active)) {
      handleSearch();
    }
  }, [active, state.active]);

  const handleTabChange = (active) => {
    setState((state) => {
      return {
        ...state,
        active: active,
      };
    });
  };

  const loadMoreMedia = () => {
    let totalPages = 30;
    let currentPage = pagination.currentPage;
    let nextPage = currentPage + 1;

    if (loading || nextPage > totalPages) {
      return;
    }
    const request = {
      mediaType: state.active,
      mediaLibraryType: mediaLibraryType,
      orientation: orientation,
      page: nextPage,
      query: query
    };

    dispatch(fetchingExternalLibrary(request));
  };

  const handleSearch = (clearQuery) => {
    // This condition fetches the data when the search is cleared
    const updatedQuery = clearQuery !== undefined ? encodeURIComponent(clearQuery) : query;

    var request = {
      mediaType: state.active,
      mediaLibraryType: mediaLibraryType,
      orientation: orientation,
      query: updatedQuery,
      page: 0
    };
    dispatch(fetchingExternalLibrary(request));
  };

  useEffect(() => {
    if (!loaded && active === 'pexels') {
      const request = {
        mediaType: state.active,
        mediaLibraryType: mediaLibraryType,
        orientation: orientation,
        query: query,
        page: 0
      };

      dispatch(fetchingExternalLibrary(request));
    }
  }, [active]);

  const handlePreview = (item) => {
    setSelectedImage(item);
    setPreview(true);
  };

  const handleOnClose = () => {
    setPreview(false);
    onClose();
  };


  const renderTabsIcon = (icon, title) => {
    return (
      <div className={styles.tabsButton}>
        <i className={`cleverstory-${icon}`} />
        <span className={styles.tabsTitle}>{title}</span>
      </div>
    )
  }

  return (
    <>
      {preview && (
        <PreviewExternalMedia
          show={preview}
          onSelect={onSelect}
          setShow={setPreview}
          selectedMedia={selectedImage}
          onClose={handleOnClose}
          editorSession={editorSession}
          target={target}
        />
      )}

      <div className={styles.externalMediaPalette}>
        <Tab.Container
          id="left-tabs-example"
          defaultActiveKey="image"
          onSelect={handleTabChange}
        >
          <div className={cx(styles.palatteHeader, "handle")}>
            <Nav className={styles.navigationTabs}>
              <Nav.Item>
                <Nav.Link active={state.active === "image"} eventKey="image">
                  {renderTabsIcon("background", "images")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link active={state.active === "video"} eventKey="video">
                  {renderTabsIcon("video", "videos")}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
          <div className={styles.searchWrapper}>
            <div className={styles.search}>
              <InputGroup className={styles.searchBox}>
                <Form.Control
                  className={styles.searchInput}
                  placeholder="Search in pexels"
                  aria-label="Search in pexels"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-describedby="Pexel search"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
                <InputGroup.Text
                  className={styles.searchIcon}
                  id="basic-addon1"
                  onClick={handleSearch}
                >
                  {loading && searchQuery ? (
                    <i
                      className={cx(styles.loadIcon, "cleverstory-spinloader")}
                    />
                  ) : (
                    <i className="cleverstory-search" />
                  )}
                </InputGroup.Text>
              </InputGroup>
            </div>
          </div>
          <div className={styles.tabContentView}>
            {loading && mediaListMemo.length === 0 ? (
              <div className={styles.shortLoaderContainer}>
                <ShortLoader
                  className={styles.loadingAnimation}
                  title="Loading..."
                />
              </div>
            ) : (
              mediaListMemo.length !== 0 && (
                <InfiniteScroll
                  className={styles.scrollable}
                  loading={loading}
                  hasMore={hasMoreItems}
                  loadMore={() => {
                    loadMoreMedia();
                  }}
                  pageStart={0}
                  threshold={250}
                >
                  <div className={styles.grid}>
                    {mediaListMemo.map((media, index) => {
                      return (
                        <ExternalMedia
                          key={index}
                          media={media}
                          onPreviewClick={() => handlePreview(media)}
                        />
                      );
                    })}
                  </div>
                </InfiniteScroll>
              )
            )}
          </div>

          {!loading && mediaListMemo.length == 0 && searchQuery != "" && (
            <>
              <div className={styles.errorMessageContent}>
                <p className={styles.errorMessageTitle}>
                  We couldn’t find anything for "{searchQuery}".
                  <br /> Try to refine your search.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    handleSearch("");
                  }}
                  className={styles.errorMessageButton}
                >
                  Clear Search
                </button>
              </div>
            </>
          )}
        </Tab.Container>
      </div>
    </>
  );
};

const UploadContent = ({ openMediaLibrary, storyboard, mediaTypes }) => {
  return (
    <Button onClick={openMediaLibrary} className={styles.uploadMediaBtn}>
      <i className={cx("cleverstory-grid-view", styles.uploadIcon)} />Media Library
    </Button>
  );
};